interface EnvVariable {
  key: string;
  required: boolean;
  description: string;
  validate?: (value: string) => boolean;
  defaultValue?: string;
}

interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  missing: string[];
}

/**
 * Environment variables configuration
 */
const ENV_VARIABLES: EnvVariable[] = [
  // Supabase Configuration
  {
    key: 'VITE_SUPABASE_URL',
    required: true,
    description: 'Supabase project URL',
    validate: (value) => value.startsWith('https://') && value.includes('.supabase.co')
  },
  {
    key: 'VITE_SUPABASE_ANON_KEY',
    required: true,
    description: 'Supabase anonymous/public key',
    validate: (value) => value.length > 20 // Basic length check
  },

  // Stripe Configuration
  {
    key: 'VITE_STRIPE_PUBLISHABLE_KEY',
    required: false, // Made optional for development
    description: 'Stripe publishable key for payment processing',
    validate: (value) => value.startsWith('pk_test_') || value.startsWith('pk_live_')
  },

  // Pricing Configuration
  {
    key: 'VITE_PRICING_REAL_ESTATE_BASE_FEE',
    required: false,
    description: 'Base monthly fee for real estate industry',
    defaultValue: '7.99',
    validate: (value) => !isNaN(parseFloat(value)) && parseFloat(value) >= 0
  },
  {
    key: 'VITE_PRICING_SERVICE_BASE_FEE',
    required: false,
    description: 'Base monthly fee for service industry',
    defaultValue: '9.99',
    validate: (value) => !isNaN(parseFloat(value)) && parseFloat(value) >= 0
  },
  {
    key: 'VITE_PRICING_PER_USER_COST',
    required: false,
    description: 'Cost per additional user beyond included users',
    defaultValue: '5.00',
    validate: (value) => !isNaN(parseFloat(value)) && parseFloat(value) >= 0
  },
  {
    key: 'VITE_PRICING_PER_FACILITY_COST',
    required: false,
    description: 'Cost per facility or service',
    defaultValue: '10.00',
    validate: (value) => !isNaN(parseFloat(value)) && parseFloat(value) >= 0
  },
  {
    key: 'VITE_PRICING_USERS_INCLUDED_IN_BASE',
    required: false,
    description: 'Number of users included in base fee',
    defaultValue: '1',
    validate: (value) => !isNaN(parseInt(value)) && parseInt(value) >= 0
  },

  // Address API Configuration
  {
    key: 'VITE_ADDRESS_API_BASE_URL',
    required: false,
    description: 'Base URL for address autocomplete service',
    defaultValue: 'https://nominatim.openstreetmap.org',
    validate: (value) => value.startsWith('http')
  }
];

/**
 * Validate all environment variables
 */
export function validateEnvironment(): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  const missing: string[] = [];

  ENV_VARIABLES.forEach(envVar => {
    const value = import.meta.env[envVar.key];

    // Check if required variable is missing
    if (envVar.required && (!value || value === `your_${envVar.key.toLowerCase().replace('vite_', '')}`)) {
      errors.push(`${envVar.key}: ${envVar.description} is required but missing or not configured`);
      missing.push(envVar.key);
      return;
    }

    // Check if optional variable has a placeholder value
    if (!envVar.required && value && value === `your_${envVar.key.toLowerCase().replace('vite_', '')}`) {
      warnings.push(`${envVar.key}: Using placeholder value. Please configure for production use.`);
      return;
    }

    // Validate format if validator is provided
    if (value && envVar.validate && !envVar.validate(value)) {
      errors.push(`${envVar.key}: Invalid format. ${envVar.description} appears to be incorrectly configured.`);
      return;
    }

    // Check for default values in production
    if (import.meta.env.PROD && !value && envVar.defaultValue) {
      warnings.push(`${envVar.key}: Using default value in production. Consider setting explicitly.`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    missing
  };
}

/**
 * Get environment variable with fallback to default
 */
export function getEnvVar(key: string): string | undefined {
  const envVar = ENV_VARIABLES.find(v => v.key === key);
  
  // Try to get from import.meta.env (Vite/browser) or process.env (Node.js)
  let value: string | undefined;
  
  // Check for Node.js environment first
  if (typeof process !== 'undefined' && process.env) {
    value = process.env[key];
  } 
  // Check for browser environment
  else if (typeof window !== 'undefined') {
    try {
      value = (import.meta as any).env[key];
    } catch {
      // Ignore errors
    }
  }

  if (value && value !== `your_${key.toLowerCase().replace('vite_', '')}`) {
    return value;
  }

  return envVar?.defaultValue;
}

/**
 * Initialize environment validation on app startup
 */
export function initializeEnvironmentValidation(): void {
  const result = validateEnvironment();

  if (!result.isValid) {
    console.error('❌ Environment validation failed:');
    result.errors.forEach(error => console.error(`  - ${error}`));

    // In development, show warnings but don't crash
    if (import.meta.env.DEV) {
      console.warn('⚠️ Application may not function correctly with missing configuration.');
    } else {
      // In production, throw error for critical missing variables
      throw new Error(`Environment validation failed. Missing required configuration: ${result.missing.join(', ')}`);
    }
  }

  if (result.warnings.length > 0) {
    console.warn('⚠️ Environment validation warnings:');
    result.warnings.forEach(warning => console.warn(`  - ${warning}`));
  }

  if (result.isValid && result.warnings.length === 0) {
    console.log('✅ Environment validation passed');
  }
}

/**
 * Get validation status for a specific environment variable
 */
export function getEnvVarStatus(key: string): {
  configured: boolean;
  valid: boolean;
  value?: string;
  error?: string;
} {
  const envVar = ENV_VARIABLES.find(v => v.key === key);
  if (!envVar) {
    return { configured: false, valid: false, error: 'Unknown environment variable' };
  }

  const value = import.meta.env[key];
  const hasValue = value && value !== `your_${key.toLowerCase().replace('vite_', '')}`;

  if (envVar.required && !hasValue) {
    return { configured: false, valid: false, error: 'Required but missing' };
  }

  if (hasValue && envVar.validate && !envVar.validate(value)) {
    return { configured: true, valid: false, value, error: 'Invalid format' };
  }

  return { configured: !!hasValue, valid: true, value: hasValue ? value : envVar.defaultValue };
}